import { Injectable } from '@angular/core';
import { catchError, from, map, Observable, of } from 'rxjs';
import {
  UserProfile,
  UserProfileFirebase,
} from '../../../models/user-profile.model';
import { DbService } from 'src/app/core/services/db.service';
import { toDate, toTimestamp } from 'src/app/shared/utils/firestore-timestamp-utils';

@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  constructor(private db: DbService) {}

  getUserProfile(uid: string): Observable<UserProfile | null> {
    return this.db.doc$<UserProfileFirebase>(`user/${uid}`).pipe(
      map((firebaseUser) => this.transformFromFirebase(firebaseUser)),
      catchError((error) => {
        // If the document doesn't exist, return null instead of throwing an error
        if (error.message?.includes('does not exist')) {
          return of(null);
        }
        // For other errors, rethrow them
        throw error;
      })
    );
  }

  updateUserProfile(user: Partial<UserProfile>): Observable<{ id?: string }> {
    const firebaseUser = this.transformToFirebase(user);
    return from(this.db.updateAt(`user/${user.uid}`, firebaseUser));
  }

  private transformFromFirebase(
    firebaseUser: UserProfileFirebase
  ): UserProfile {
    return {
      ...firebaseUser,
      account_startedAt: toDate(firebaseUser.account_startedAt),
      account_deletionRequestedAt: firebaseUser.account_deletionRequestedAt
        ? toDate(firebaseUser.account_deletionRequestedAt)
        : null,
    };
  }

  private transformToFirebase(
    user: Partial<UserProfile>
  ): Partial<UserProfileFirebase> {
    return {
      ...user,
      account_startedAt: toTimestamp(user.account_startedAt),
      account_deletionRequestedAt: user.account_deletionRequestedAt
        ? toTimestamp(user.account_deletionRequestedAt)
        : null,
    };
  }
}
